/**
 * useSimulatorParams
 *
 * Provides deep-linkable state for the Savings Simulator via URL search parameters.
 *
 * URL parameter reference:
 *
 *   p   – pool ID
 *   d   – selected date (YYYY-MM-DD); view is centred on this day
 *   v   – view mode: 'd' (day, default), 'w' (week), 'm' (month)
 *   wk  – ISO week (e.g. '2026-W14'); used in week view
 *   mo  – year-month (e.g. '2026-04'); used in month view
 *   fr  – flat rate (€/kWh, 2 decimal float)
 *   go  – grid offset (€/kWh, 2 decimal float)
 *   ef  – efficiency (0–1, 2 decimal float)
 */

import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  format,
  parseISO,
  isValid,
  startOfDay,
  endOfDay,
  startOfISOWeek,
  endOfISOWeek,
  startOfMonth,
  endOfMonth,
  setISOWeek,
  setISOWeekYear,
  getISOWeek,
  getISOWeekYear,
} from 'date-fns'

export type SimView = 'day' | 'week' | 'month'

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULTS = {
  poolID: '',
  flatRate: 0.24,
  offset: 0.21,
  efficiency: 0.95,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFloat(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key)
  if (raw === null) return fallback
  const n = parseFloat(raw)
  return isNaN(n) ? fallback : n
}

function setParamOrDelete(
  params: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string
) {
  if (value === defaultValue) {
    params.delete(key)
  } else {
    params.set(key, value)
  }
}

/** Format a Date as ISO week string, e.g. "2026-W14" */
function formatIsoWeek(d: Date): string {
  return `${getISOWeekYear(d)}-W${String(getISOWeek(d)).padStart(2, '0')}`
}

/** Parse "2026-W14" → start-of-week Date (Monday). Falls back to current week. */
function parseIsoWeekStr(wk: string): Date {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(wk)
  if (!m) return startOfISOWeek(new Date())
  const year = parseInt(m[1])
  const week = parseInt(m[2])
  return startOfISOWeek(setISOWeek(setISOWeekYear(new Date(), year), week))
}

/** Parse "2026-04" → first day of that month. Falls back to current month. */
function parseIsoMonthStr(mo: string): Date {
  const parsed = parseISO(`${mo}-01`)
  return isValid(parsed) ? startOfMonth(parsed) : startOfMonth(new Date())
}

/**
 * Returns the display/filter range for the current view — the exact period
 * boundaries (one day / one week / one month).
 */
export function resolveSimRange(
  view: SimView,
  selectedDay: Date,
  isoWeek: string,
  isoMonth: string
): { start: Date; end: Date } {
  if (view === 'week') {
    const weekStart = parseIsoWeekStr(isoWeek)
    return { start: weekStart, end: endOfISOWeek(weekStart) }
  }
  if (view === 'month') {
    const monthStart = parseIsoMonthStr(isoMonth)
    return { start: monthStart, end: endOfMonth(monthStart) }
  }
  return { start: startOfDay(selectedDay), end: endOfDay(selectedDay) }
}

/**
 * Returns 1–2 fetch ranges for API/cache purposes.
 *
 * For week/month views ranges are always aligned to full calendar month(s), so
 * that switching between a week and its containing month is a React Query cache
 * hit. A week that spans two months returns two separate month ranges — one per
 * month — so each is independently cached and reused when those months are
 * viewed individually.
 *
 * Day mode returns a single-element array (unchanged behaviour).
 */
export function resolveSimFetchRange(
  view: SimView,
  selectedDay: Date,
  isoWeek: string,
  isoMonth: string
): [{ start: Date; end: Date }] | [{ start: Date; end: Date }, { start: Date; end: Date }] {
  if (view === 'week') {
    const weekStart = parseIsoWeekStr(isoWeek)
    const weekEnd = endOfISOWeek(weekStart)
    const monthA = format(weekStart, 'yyyy-MM')
    const monthB = format(weekEnd, 'yyyy-MM')
    if (monthA !== monthB) {
      // Week spans a month boundary — return two separate month ranges so each
      // can be independently cached and shared with month-view queries.
      return [
        { start: startOfMonth(weekStart), end: endOfMonth(weekStart) },
        { start: startOfMonth(weekEnd),   end: endOfMonth(weekEnd)   },
      ]
    }
    return [{ start: startOfMonth(weekStart), end: endOfMonth(weekStart) }]
  }
  if (view === 'month') {
    const monthStart = parseIsoMonthStr(isoMonth)
    return [{ start: monthStart, end: endOfMonth(monthStart) }]
  }
  return [{ start: startOfDay(selectedDay), end: endOfDay(selectedDay) }]
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSimulatorParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Read ────────────────────────────────────────────────────────────────────

  const poolID = searchParams.get('p') ?? DEFAULTS.poolID

  const rawDate = searchParams.get('d')
  const selectedDay: Date = (() => {
    if (!rawDate) return startOfDay(new Date())
    const parsed = parseISO(rawDate)
    return isValid(parsed) ? startOfDay(parsed) : startOfDay(new Date())
  })()

  const flatRate = getFloat(searchParams, 'fr', DEFAULTS.flatRate)
  const offset = getFloat(searchParams, 'go', DEFAULTS.offset)
  const efficiency = getFloat(searchParams, 'ef', DEFAULTS.efficiency)

  const rawView = searchParams.get('v')
  const view: SimView =
    rawView === 'w' ? 'week' : rawView === 'm' ? 'month' : 'day'

  const isoWeek: string = searchParams.get('wk') ?? formatIsoWeek(selectedDay)
  const isoMonth: string = searchParams.get('mo') ?? format(selectedDay, 'yyyy-MM')

  // ── Write ───────────────────────────────────────────────────────────────────

  const setPoolID = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'p', id, DEFAULTS.poolID)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setSelectedDay = useCallback(
    (d: Date) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const iso = format(d, 'yyyy-MM-dd')
          const defaultIso = format(startOfDay(new Date()), 'yyyy-MM-dd')
          setParamOrDelete(next, 'd', iso, defaultIso)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setFlatRate = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'fr', v.toFixed(2), DEFAULTS.flatRate.toFixed(2))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setOffset = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'go', v.toFixed(2), DEFAULTS.offset.toFixed(2))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setEfficiency = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'ef', v.toFixed(2), DEFAULTS.efficiency.toFixed(2))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // When switching to week/month view, seed the period param from selectedDay
  const setView = useCallback(
    (v: SimView, anchorDay: Date) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (v === 'day') {
            next.delete('v')
            next.delete('wk')
            next.delete('mo')
          } else if (v === 'week') {
            next.set('v', 'w')
            next.set('wk', formatIsoWeek(anchorDay))
            next.delete('d')
            next.delete('mo')
          } else {
            next.set('v', 'm')
            next.set('mo', format(anchorDay, 'yyyy-MM'))
            next.delete('d')
            next.delete('wk')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setIsoWeek = useCallback(
    (wk: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('wk', wk)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setIsoMonth = useCallback(
    (mo: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('mo', mo)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return {
    poolID,
    selectedDay,
    flatRate,
    offset,
    efficiency,
    view,
    isoWeek,
    isoMonth,
    setPoolID,
    setSelectedDay,
    setFlatRate,
    setOffset,
    setEfficiency,
    setView,
    setIsoWeek,
    setIsoMonth,
  }
}
